import {
  IAdvancedFraudTrendCheck,
  IAdvancedFraudTrendPattern,
  IFraudPatternEvidencePattern,
  IVisualSignal,
  IVisualTextBoxes,
  VisualBbox
} from 'src/ts/interfaces/listCheck';
import { textBoxesCopy } from './textBoxesCopy';

export type KindColor = 'red' | 'teal' | 'purple' | 'magenta' | 'neutral';

export interface KindStyle {
  kind: string;
  color: KindColor;
  headline: string;
  description: string;
  note?: string;
  note_at?: 'before' | 'after';
}

export interface BoxMark {
  kind: string;
  rule: boolean;
  signal: IVisualSignal | null;
}

export interface Piece {
  bbox: VisualBbox;
  text: string;
  marks: BoxMark[];
}

export interface Orphan {
  bbox: VisualBbox;
  mark: BoxMark;
}

export interface PageModel {
  page: number;
  width: number;
  height: number;
  pieces: Piece[];
  orphans: Orphan[];
}

export interface LegendEntry extends KindStyle {
  count: number;
  sample: string;
  removed: boolean;
}

export interface AlsoFoundEntry {
  key: string;
  label: string;
  count: number | null;
}

export interface Chip {
  bbox: VisualBbox;
  text: string;
  mark: BoxMark | null;
  orphan: boolean;
}

export interface Row {
  page: number;
  chips: Chip[];
  mark: BoxMark | null;
}

export interface TextBoxesModel {
  providerName: string;
  font: 'mono' | 'sans';
  pages: PageModel[];
  legend: LegendEntry[];
  normalSample: string;
  alsoFound: AlsoFoundEntry[];
  kindStyle: (kind: string) => KindStyle;
}

export interface TextBoxesSource {
  pattern: IAdvancedFraudTrendPattern;
  found: IFraudPatternEvidencePattern | null;
  providerName: string;
  font: 'mono' | 'sans';
}

const SAMPLE_LENGTH = 14;
const ROW_TOLERANCE = 2;
const TRAILING_ZERO = 'trailing_zero';
const COLORS: KindColor[] = ['red', 'teal', 'purple', 'magenta'];

const SIGNAL_VALUES: (keyof IVisualSignal)[] = ['gap_em', 'width_px', 'move_px'];

export const noteFor = (style: KindStyle, signal: IVisualSignal | null): string | null => {
  if (!style.note) return null;
  return style.note
    .replace(/\{(\w+)\}/g, (_, field: string) => {
      const value = SIGNAL_VALUES.includes(field as keyof IVisualSignal)
        ? signal?.[field as keyof IVisualSignal]
        : undefined;
      return typeof value === 'number' ? value.toLocaleString('en-US') : '';
    })
    .replace(/\s+/g, ' ')
    .trim();
};

export const blankText = (text: string) => '·'.repeat(Math.min(Math.max(text.length, 3), 10));

const clip = (text: string) =>
  text.length > SAMPLE_LENGTH ? `${text.slice(0, SAMPLE_LENGTH - 1)}…` : text;

export const primaryMark = (marks: BoxMark[]): BoxMark | null => marks[0] ?? null;

const colorOf = (color: string | undefined): KindColor =>
  COLORS.find((known) => known === color) ?? 'neutral';

const styleOf = (check: IAdvancedFraudTrendCheck): KindStyle => ({
  kind: check.signals?.[0] ?? check.key,
  color: colorOf(check.color),
  headline: check.headline ?? check.label,
  description: check.description ?? '',
  note: check.note,
  note_at: check.note_at
});

export const buildModel = (
  visual: IVisualTextBoxes,
  { pattern, found, providerName, font }: TextBoxesSource
): TextBoxesModel => {
  const signalled = pattern.checks.filter((check) => check.signals?.length);
  const kindOrder = signalled.map((check) => check.signals?.[0] as string);
  const catalogFor = (kind: string) => signalled.find((check) => check.signals?.[0] === kind);
  const kindOf = (type: string) =>
    signalled.find((check) => check.signals?.includes(type))?.signals?.[0] ?? type;
  const rank = (mark: BoxMark) => {
    const index = kindOrder.indexOf(mark.kind);
    return (mark.rule ? 0 : kindOrder.length + 1) + (index === -1 ? kindOrder.length : index);
  };
  const kindStyle = (kind: string): KindStyle => {
    const check = catalogFor(kind);
    return check
      ? styleOf(check)
      : { kind, color: 'neutral', headline: textBoxesCopy.flaggedPiece, description: '' };
  };
  const trailingZero = catalogFor(TRAILING_ZERO);

  const counts = new Map<string, number>();
  const tally = (kind: string) => counts.set(kind, (counts.get(kind) ?? 0) + 1);

  const pages: PageModel[] = (visual.pages ?? []).map((source) => {
    const signals = (source.signals ?? []).filter(
      (signal) => !kindOrder.length || kindOrder.includes(kindOf(signal.type))
    );
    const text = source.text ?? [];
    const ids = new Set(text.map((piece) => piece.id));
    signals.forEach((signal) => tally(kindOf(signal.type)));
    const pieces = text.map((piece) => {
      const marks: BoxMark[] = signals
        .filter((signal) => (signal.text_ids ?? []).includes(piece.id))
        .map((signal) => ({ kind: kindOf(signal.type), rule: signal.role === 'rule', signal }));
      if (piece.trailing_zero) {
        tally(TRAILING_ZERO);
        marks.push({ kind: TRAILING_ZERO, rule: trailingZero?.role === 'rule', signal: null });
      }
      return { bbox: piece.bbox, text: piece.text, marks: marks.sort((a, b) => rank(a) - rank(b)) };
    });
    const orphans = signals
      .filter((signal) => !(signal.text_ids ?? []).some((id) => ids.has(id)))
      .map((signal) => ({
        bbox: signal.bbox,
        mark: { kind: kindOf(signal.type), rule: signal.role === 'rule', signal }
      }));
    return { page: source.number, width: source.width, height: source.height, pieces, orphans };
  });

  const allPieces = pages.flatMap((page) => page.pieces);
  const sampleFor = (kind: string) => {
    const piece = allPieces.find((p) => p.marks.some((mark) => mark.kind === kind));
    if (!piece) return '';
    return piece.text.trim() ? clip(piece.text.trim()) : blankText(piece.text);
  };
  const normal = allPieces.find((p) => p.marks.length === 0 && p.text.trim());

  const legend = kindOrder
    .filter((kind) => counts.has(kind))
    .map((kind) => {
      const sample = sampleFor(kind);
      return { ...kindStyle(kind), count: counts.get(kind) ?? 0, sample, removed: !sample };
    });

  const alsoFound = pattern.checks
    .filter((check) => check.role === 'support' && !counts.has(check.signals?.[0] ?? ''))
    .flatMap((check) => {
      const result = found?.checks?.find((item) => item.name === check.key);
      return result?.status === 'fired'
        ? [{ key: check.key, label: check.label, count: result.count }]
        : [];
    });

  return {
    providerName,
    font,
    pages,
    legend,
    normalSample: normal ? clip(normal.text.trim()) : '',
    alsoFound,
    kindStyle
  };
};

export const flaggedOn = (page: PageModel): number =>
  page.pieces.filter((piece) => piece.marks.length > 0).length + page.orphans.length;

export const rowsOf = (page: PageModel): Row[] => {
  const chips: Chip[] = [
    ...page.pieces.map((piece) => ({
      bbox: piece.bbox,
      text: piece.text,
      mark: primaryMark(piece.marks),
      orphan: false
    })),
    ...page.orphans.map((orphan) => ({
      bbox: orphan.bbox,
      text: '',
      mark: orphan.mark,
      orphan: true
    }))
  ].sort((a, b) => a.bbox[3] - b.bbox[3] || a.bbox[0] - b.bbox[0]);

  const rows: { bottom: number; chips: Chip[] }[] = [];
  chips.forEach((chip) => {
    const last = rows[rows.length - 1];
    if (last && Math.abs(chip.bbox[3] - last.bottom) <= ROW_TOLERANCE) last.chips.push(chip);
    else rows.push({ bottom: chip.bbox[3], chips: [chip] });
  });

  return rows.map((row) => {
    const sorted = row.chips.sort((a, b) => a.bbox[0] - b.bbox[0]);
    return {
      page: page.page,
      chips: sorted,
      mark: sorted.find((chip) => chip.mark)?.mark ?? null
    };
  });
};
